'use client';

import { useEffect, useRef } from 'react';
import type { Chat } from '../../types/index.js';
import type { FilePart } from '../../lib/db/types.js';
import type { AttachedSkill } from '../skills/types.js';

interface UseChatPageEffectsParams {
  urlChatId: string | null;
  selectedChatId: string | null;
  messagesChat: Chat | null | undefined;
  launchRequest: { query: string; nonce: number } | null;
  setSelectedChatId: (id: string | null) => void;
  setSelectedChat: (chat: Chat | null) => void;
  setUrlChatId: (id: string | null) => void;
  setInitialQuery: (query: string) => void;
  setInitialFiles: (files: FilePart[]) => void;
  setInitialSkill: (skill: AttachedSkill | null) => void;
  setError: (error: string | null) => void;
  setShowShareModal: (show: boolean) => void;
  closeModal: (id: string) => void;
  closeDrawer: (id: string) => void;
  handleStartChatWithQuery: (query: string) => Promise<void>;
}

/**
 * ChatPage lifecycle effects: URL↔selection sync, fetched-chat sync,
 * chat-switch UI cleanup, and one-shot launch (omnibox/deep-link). Extracted
 * from ChatPage; behaviour unchanged.
 */
export function useChatPageEffects({
  urlChatId,
  selectedChatId,
  messagesChat,
  launchRequest,
  setSelectedChatId,
  setSelectedChat,
  setUrlChatId,
  setInitialQuery,
  setInitialFiles,
  setInitialSkill,
  setError,
  setShowShareModal,
  closeModal,
  closeDrawer,
  handleStartChatWithQuery,
}: UseChatPageEffectsParams): void {
  const previousSelectedChatIdRef = useRef<string | null>(null);
  const processedLaunchNonceRef = useRef<number | null>(null);

  // Sync URL chat ID with selected chat ID
  useEffect(() => {
    if (urlChatId && urlChatId !== selectedChatId) {
      setSelectedChatId(urlChatId);
      setSelectedChat(null);
    } else if (!urlChatId && selectedChatId) {
      setSelectedChatId(null);
      setSelectedChat(null);
    }
  }, [urlChatId, selectedChatId, setSelectedChatId, setSelectedChat]);

  // Sync fetched chat data to selectedChat state
  useEffect(() => {
    if (selectedChatId && messagesChat) {
      setSelectedChat(messagesChat);
    }
  }, [selectedChatId, messagesChat, setSelectedChat]);

  // Comprehensive cleanup when switching from one chat to another.
  useEffect(() => {
    if (selectedChatId && selectedChatId !== previousSelectedChatIdRef.current) {
      const wasExistingChat = previousSelectedChatIdRef.current != null;
      if (wasExistingChat) {
        setInitialQuery('');
        setInitialFiles([]);
        setInitialSkill(null);
        setError(null);
        setShowShareModal(false);
        closeModal('chat-settings');
        closeModal('chat-capabilities');
        closeDrawer('mobile-sidebar-drawer');
      }
      previousSelectedChatIdRef.current = selectedChatId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatId, setError, closeDrawer]);

  // Omnibox / deep-link: reset to a fresh chat and optionally send the first message.
  useEffect(() => {
    if (!launchRequest) return;
    const { query, nonce } = launchRequest;
    if (processedLaunchNonceRef.current === nonce) return;
    processedLaunchNonceRef.current = nonce;

    void (async () => {
      setUrlChatId(null);
      setSelectedChatId(null);
      setSelectedChat(null);
      const q = query.trim();
      if (!q) return;
      await new Promise<void>((resolve) => {
        queueMicrotask(() => resolve());
      });
      await handleStartChatWithQuery(q);
    })();
  }, [launchRequest, handleStartChatWithQuery, setUrlChatId, setSelectedChatId, setSelectedChat]);
}
